import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../common/ThemeToggle';
import '../../styles/sidebar.css';

const Sidebar = ({
  activeTab,
  setActiveTab,
  activeSessions = [],
  alerts = [],
  isMobileOpen = false,
  onMobileClose
}) => {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.isPoolManager);
  };

  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
    if (onMobileClose) {
      onMobileClose();
    }
  };

  const menuItems = [
    {
      id: 'solana-tokens',
      icon: '⭐',
      label: 'Top Tokens',
      description: 'Trending tokens'
    },
    {
      id: 'dashboard',
      icon: '🏠',
      label: 'Dashboard',
      description: 'Home overview'
    },
    {
      id: 'manual',
      icon: '⚡',
      label: 'Advanced Trading',
      description: 'Full trading suite'
    },
    {
      id: 'automated',
      icon: '💼',
      label: 'Wallet Manager',
      description: 'Manage wallets'
    },
    {
      id: 'sessions',
      icon: '💰',
      label: 'My Assets',
      description: 'Portfolio overview',
      badge: activeSessions.length
    },
    {
      id: 'price-alerts',
      icon: '🔔',
      label: 'Price Alerts',
      description: 'Manage alerts',
      badge: alerts.filter(a => a.is_active && !a.triggered_at).length
    },
    // {
    //   id: 'opportunities',
    //   icon: '📈',
    //   label: 'Trading Opportunities',
    //   description: 'Market opportunities',
    //   highlight: true
    // },
    // {
    //   id: 'investment',
    //   icon: '🏦',
    //   label: 'Investment Pools',
    //   description: 'Professional trading',
    //   highlight: true
    // },
    {
      id: 'admin',
      icon: '⚙️',
      label: 'Pool Management',
      description: 'Admin dashboard',
      adminOnly: true
    },
    {
      id: 'history',
      icon: '📈',
      label: 'Trade History',
      description: 'Past transactions'
    },
    {
      id: 'analytics',
      icon: '📊',
      label: 'Token Watchlist',
      description: 'Track favorites'
    },
    {
      id: 'settings',
      icon: '⚙️',
      label: 'Settings',
      description: 'App preferences'
    }
  ];

  return (
    <>
      {isMobileOpen && <div className="sidebar-overlay" onClick={onMobileClose}></div>}
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        {/* Sidebar Header */}
        <div className="sidebar-header" style={{
          zIndex: 888,
        }}>
          <div className="logo-section" style={{
          zIndex: 888,
        }}>
            <div className="logo-icon">🚀</div>
            {!isCollapsed && (
              <div className="logo-text">
                <h3>SafePump</h3>
                <span>Trading Bot</span>
              </div>
            )}
          </div>
          <button
            className="collapse-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? '→' : '←'}
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">
              {!isCollapsed && 'Discover'}
            </div>
            {menuItems.slice(0, 2).map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleNavClick(item.id)}
                title={isCollapsed ? item.label : ''}
              >
                <span className="nav-icon">{item.icon}</span>
                {!isCollapsed && (
                  <div className="nav-content">
                    <span className="nav-label">{item.label}</span>
                    <span className="nav-description">{item.description}</span>
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">
              {!isCollapsed && 'Trading'}
            </div>
            {menuItems.slice(2, 5).map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleNavClick(item.id)}
                title={isCollapsed ? item.label : ''}
              >
                <span className="nav-icon">{item.icon}</span>
                {!isCollapsed && (
                  <div className="nav-content">
                    <span className="nav-label">{item.label}</span>
                    <span className="nav-description">{item.description}</span>
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">
              {!isCollapsed && 'Monitoring'}
            </div>
            {menuItems.slice(5, 6).map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleNavClick(item.id)}
                title={isCollapsed ? item.label : ''}
              >
                <span className="nav-icon">{item.icon}</span>
                {!isCollapsed && (
                  <div className="nav-content">
                    <span className="nav-label">{item.label}</span>
                    <span className="nav-description">{item.description}</span>
                  </div>
                )}
                {item.badge > 0 && (
                  <span className="nav-badge">{item.badge}</span>
                )}
              </button>
            ))}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">
              {!isCollapsed && 'Analytics'}
            </div>
            {menuItems.slice(6).filter(item => !item.adminOnly || isAdmin()).map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''} ${item.highlight ? 'highlight' : ''} ${item.adminOnly ? 'admin-only' : ''}`}
                onClick={() => handleNavClick(item.id)}
                title={isCollapsed ? item.label : ''}
              >
                <span className="nav-icon">{item.icon}</span>
                {!isCollapsed && (
                  <div className="nav-content">
                    <span className="nav-label">{item.label}</span>
                    <span className="nav-description">{item.description}</span>
                  </div>
                )}
                {item.highlight && !isCollapsed && (
                  <span className="nav-highlight">NEW</span>
                )}
                {item.adminOnly && !isCollapsed && (
                  <span className="nav-admin-badge">ADMIN</span>
                )}
              </button>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          {!isCollapsed && (
            <div className="quick-stats">
              <div className="stat-item" >
                <span className="stat-icon" style={{ fontSize: 17 }}>🔄</span>
                <div className="stat-content" style={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
                  <span className="stat-value" style={{ fontSize: 15 }}>{activeSessions.length}</span>
                  <span className="stat-label" style={{ fontSize: 10.7 }}>Active</span>
                </div>
              </div>
              <div className="stat-item">
                <span className="stat-icon" style={{ fontSize: 17 }}>🔔</span>
                <div className="stat-content" style={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
                  <span className="stat-value" style={{ fontSize: 15 }}>{alerts.filter(a => !a.read).length}</span>
                  <span className="stat-label" style={{ fontSize: 10.7 }}>Alerts</span>
                </div>
              </div>
            </div>
          )}

          <div className="sidebar-actions">
            <div className="theme-toggle-wrapper">
              <ThemeToggle
                className="sidebar-theme-toggle"
                showLabel={!isCollapsed}
              />
            </div>
            <button
              className="action-btn emergency"
              title="Emergency Stop All"
            >
              {isCollapsed ? '🛑' : '🛑 Emergency Stop'}
            </button>
          </div>
        </div>
      </div> {/* <-- Add this closing tag for the sidebar main div */}
    </>
  );
};

export default Sidebar;